import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { publicFileUrl } from "@/lib/api";
import { CreativeReviewCard } from "@/components/CreativeReviewCard";
import { Zap, Loader2, LinkIcon } from "lucide-react";
import { toast } from "sonner";

const PublicBoard = () => {
  const { token } = useParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/batch/${token}`);
      setBoard(r.data);
    } catch (e) { setError(true); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (creative_id, decision, comment, name) => {
    try {
      await api.post(`/public/batch/${token}/feedback`, { creative_id, decision, comment, name });
      toast.success(decision === "Approve" ? "Approved — thank you!" : "Change request sent");
      load();
    } catch (e) { toast.error("Could not submit — this link may be disabled"); }
  };

  const resolve = (p) => publicFileUrl(token, p);

  if (loading) return <div className="min-h-screen bg-ink-900 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  if (error || !board) return (
    <div className="min-h-screen bg-ink-900 flex flex-col items-center justify-center text-center px-6">
      <div className="w-12 h-12 rounded-xl bg-ink-800 border border-ink-700 flex items-center justify-center mb-4"><LinkIcon className="w-6 h-6 text-zinc-500" /></div>
      <h1 className="font-display text-3xl font-bold">Link unavailable</h1>
      <p className="text-zinc-400 mt-2">This share link is invalid or has been disabled. Ask the agency for a fresh link.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-ink-900/80 border-b border-ink-700">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><Zap className="w-5 h-5 text-ink-900" fill="#09090B" /></div>
            <span className="font-display text-xl font-bold">SuperPumped Studio</span>
          </div>
          <span className="text-zinc-400 text-sm hidden sm:inline">Shared review board</span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-brand font-semibold uppercase tracking-wide text-sm mb-2">{board.client_name}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{board.batch_name}</h1>
        <p className="text-zinc-400 mt-2 mb-10">Review the creatives below. Approve or request changes — no account needed.</p>

        {board.creatives.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">No creatives in this batch yet.</div>
        ) : (
          <div className="masonry columns-1 sm:columns-2 lg:columns-3">
            {board.creatives.map((c) => (
              <CreativeReviewCard key={c.id} c={c} resolve={resolve} fallbackThumb={board.thumbnail} showNameField onSubmit={submit} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBoard;
